import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { features } from '@/mocks/features';
import { faqs } from '@/mocks/faqs';
import { reviews } from '@/mocks/reviews';

const heroArticles = articles.slice(0, 3);

const heroBgUrl = 'https://readdy.ai/api/search-image?query=Warm%20minimalist%20abstract%20digital%20landscape%20with%20soft%20flowing%20geometric%20shapes%2C%20cream%20and%20warm%20beige%20gradients%2C%20gentle%20atmospheric%20light%2C%20clean%20modern%20aesthetic%2C%20smooth%20curves%20and%20layered%20translucent%20elements%2C%20peaceful%20and%20inviting%20atmosphere&width=1800&height=1200&seq=hero-bg-home&orientation=landscape';

const ctaBgUrl = 'https://readdy.ai/api/search-image?query=Warm%20minimalist%20abstract%20gradient%20background%20with%20soft%20flowing%20amber%20and%20cream%20shapes%2C%20gentle%20atmospheric%20light%2C%20clean%20modern%20aesthetic%2C%20smooth%20curves%2C%20inviting%20peaceful%20atmosphere&width=1800&height=800&seq=cta-bg-home&orientation=landscape';

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '网页版免费91',
  url: 'https://avvg.cn',
  description: '无需下载安装即可直接在浏览器中免费使用的在线平台',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://avvg.cn/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: '网页版免费91 - 首页',
        description: '无需下载安装即可直接在浏览器中免费使用的在线平台',
        url: 'https://avvg.cn/',
      })}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full min-h-[600px] flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0">
              <img
                src={heroBgUrl}
                alt="网页版免费91平台背景"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-background-50"></div>
            </div>
            <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center py-20">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 font-heading leading-tight">
                网页版免费91
                <br />
                <span className="text-primary-300">无需下载 · 即开即用</span>
              </h1>
              <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
                网页版免费91是您无需下载安装即可直接在浏览器中免费使用的在线平台。支持多设备访问，操作简单流畅，立即体验网页版免费91带来的便捷服务，完全免费，随时随地畅享。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/category/online-tools"
                  className="px-8 py-3.5 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  立即免费体验
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-3.5 bg-white/15 text-white rounded-full text-sm font-semibold hover:bg-white/25 transition-all duration-300 whitespace-nowrap cursor-pointer backdrop-blur-sm"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section id="features" className="py-16 md:py-20 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                  为什么选择<strong>网页版免费91</strong>
                </h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                  六大核心优势，为您打造极致的在线使用体验
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="group bg-background-50 border border-background-200/70 rounded-lg p-6 hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-lg mb-4 group-hover:bg-primary-500 transition-colors duration-300">
                      <i className={`${feature.icon} text-xl text-primary-600 group-hover:text-white transition-colors duration-300`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading">{feature.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Categories Section */}
          <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                  热门服务分类
                </h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                  六大分类覆盖您的日常需求，点击探索更多
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group bg-background-50 rounded-lg p-6 border border-background-200/70 hover:border-primary-300 transition-all duration-300 flex items-start gap-4 cursor-pointer"
                  >
                    <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-secondary-100 rounded-lg group-hover:bg-primary-100 transition-colors duration-300">
                      <i className={`${cat.icon} text-xl text-secondary-600 group-hover:text-primary-600 transition-colors duration-300`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-foreground-900 mb-1 font-heading">{cat.name}</h3>
                      <p className="text-sm text-foreground-600 line-clamp-2 mb-2">{cat.description}</p>
                      <span className="text-xs text-foreground-400">{cat.articleCount} 篇文章</span>
                    </div>
                    <div className="flex-shrink-0 flex items-center pt-1">
                      <i className="ri-arrow-right-s-line text-foreground-400 group-hover:text-primary-500 transition-colors duration-300"></i>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Popular Articles Section */}
          <section className="py-16 md:py-20 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-10">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-2">
                    热门推荐
                  </h2>
                  <p className="text-sm text-foreground-600">精选优质内容，发现更多精彩</p>
                </div>
                <Link
                  to="/category/online-tools"
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 whitespace-nowrap"
                >
                  查看全部 <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {heroArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.cardImageUrl}
                        alt={article.title}
                        title={`${article.title} - 网页版免费91`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <span className="inline-block px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-md mb-3 font-medium">
                        {article.categoryName}
                      </span>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2 line-clamp-2 font-heading group-hover:text-primary-600 transition-colors duration-200">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 line-clamp-2 mb-3">{article.excerpt}</p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span>{article.publishDate}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i> {article.viewCount.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Reviews Section */}
          <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                  用户好评
                </h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                  听听用户怎么说网页版免费91
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {reviews.slice(0, 3).map((review, index) => (
                  <div key={index} className="bg-background-50 rounded-lg p-6 border border-background-200/70">
                    <div className="flex items-center gap-3 mb-4">
                      <img
                        src={review.avatar}
                        alt={review.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">{review.name}</p>
                        <div className="flex items-center gap-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <i
                              key={i}
                              className={`text-xs ${i < review.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-foreground-300'}`}
                            ></i>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                    <p className="text-xs text-foreground-400 mt-3">{review.date}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section id="faq" className="py-16 md:py-20 px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                  常见问题
                </h2>
                <p className="text-sm md:text-base text-foreground-600">
                  关于网页版免费91的常见疑问解答
                </p>
              </div>
              <div className="flex flex-col gap-3">
                {faqs.slice(0, 5).map((faq, index) => (
                  <div
                    key={index}
                    className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                    >
                      <h3 className="text-sm font-semibold text-foreground-900 pr-4">{faq.question}</h3>
                      <i
                        className={`text-foreground-400 text-sm flex-shrink-0 transition-transform duration-300 ${
                          openFaq === index ? 'ri-subtract-line' : 'ri-add-line'
                        }`}
                      ></i>
                    </button>
                    {openFaq === index && (
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="relative py-20 md:py-24 px-4 md:px-6 overflow-hidden">
            <div className="absolute inset-0">
              <img
                src={ctaBgUrl}
                alt=""
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-primary-600/90 to-primary-800/80"></div>
            </div>
            <div className="relative z-10 max-w-2xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-white font-heading mb-4">
                准备好开始了吗？
              </h2>
              <p className="text-white/80 text-sm md:text-base mb-8 leading-relaxed">
                加入数以万计的用户，立即体验网页版免费91带来的便捷在线服务。完全免费，无需下载。
              </p>
              <Link
                to="/category/online-tools"
                className="inline-flex items-center px-8 py-3.5 bg-white text-primary-600 rounded-full text-sm font-semibold hover:bg-white/90 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                立即免费使用 <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </section>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}